import { Alert, Button, Input, Space, Typography } from "antd";
import { type ReactNode, useState } from "react";
import {
	DELETE_RESOURCE_DESC,
	DELETE_RESOURCE_TITLE,
} from "../common/messages";
import { ResponsiveDialog } from "./responsive-dialog";

export type FrictionConfirmOptions = Omit<
	FrictionConfirmDialogProps,
	"open" | "onOpenChange"
>;

/**
 * Imperative wrapper for FrictionConfirmDialog, drop-in for Modal.confirm call
 * sites: call confirm({...}) from any handler and render {dialog} once in the JSX.
 */
export function useFrictionConfirm() {
	const [options, setOptions] = useState<FrictionConfirmOptions | null>(null);
	const dialog = options ? (
		<FrictionConfirmDialog
			open
			onOpenChange={(open) => {
				if (!open) setOptions(null);
			}}
			{...options}
		/>
	) : null;
	return { confirm: setOptions, dialog };
}

interface FrictionConfirmDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Message shown to the user, typically a constant from common/messages.ts; may be rich content
	 * (e.g. a list of what else the delete removes). */
	message?: ReactNode;
	/** Extra content below the warning, e.g. a table of what the delete affects. */
	details?: ReactNode;
	/** Dialog width; widen it when `details` holds a table. */
	width?: number;
	title?: string;
	/** Text the user must type to enable the confirm button. */
	confirmText?: string;
	confirmButtonLabel?: string;
	onConfirm: () => void;
}

/**
 * Confirmation dialog with friction: the destructive (red) confirm button stays
 * disabled until the user types the confirmation text exactly.
 */
export function FrictionConfirmDialog({
	open,
	onOpenChange,
	message = DELETE_RESOURCE_DESC,
	details,
	width = 420,
	title = DELETE_RESOURCE_TITLE,
	confirmText = "delete",
	confirmButtonLabel = "Delete",
	onConfirm,
}: FrictionConfirmDialogProps) {
	const [typed, setTyped] = useState("");
	const matches = typed === confirmText;

	const close = () => {
		setTyped("");
		onOpenChange(false);
	};

	return (
		<ResponsiveDialog
			open={open}
			onOpenChange={onOpenChange}
			width={width}
			title={title}
			onCancel={() => setTyped("")}
			footer={
				<Space>
					<Button onClick={close}>Cancel</Button>
					<Button
						type="primary"
						danger
						disabled={!matches}
						onClick={() => {
							close();
							onConfirm();
						}}
					>
						{confirmButtonLabel}
					</Button>
				</Space>
			}
		>
			<Space
				orientation="vertical"
				size="middle"
				style={{ width: "100%", paddingTop: 4 }}
			>
				<Alert type="warning" description={message} />
				{details}
				<div>
					<Typography.Text
						type="secondary"
						style={{ display: "block", marginBottom: 8, fontSize: 13 }}
					>
						Type <Typography.Text code>{confirmText}</Typography.Text> to
						confirm
					</Typography.Text>
					<Input
						autoFocus
						value={typed}
						onChange={(e) => setTyped(e.target.value)}
						placeholder={confirmText}
						onPressEnter={() => {
							if (matches) {
								close();
								onConfirm();
							}
						}}
					/>
				</div>
			</Space>
		</ResponsiveDialog>
	);
}
